import { LottieComponentProps } from 'lottie-react';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import StartupAnimation from 'src/common/components/startup-animation/startup-animation';
import { delay } from 'src/common/helpers/delay-helper';
import XLRLogger from 'src/common/helpers/logger';
import { skipAnimations } from 'src/common/helpers/short-circuit-helper';
import { getLaunchAnimation } from 'src/common/helpers/startup-animation-helper';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { ThemeContext } from 'src/emerald-theme-provider';
import {
  emeraldStartupSequence,
  r10StartupSequence,
} from 'src/xlr-configuration/business-logic/xlr-configuration-logic';
import {
  getMetaData,
  useIsEmeraldSellingService,
} from 'src/xlr-configuration/pos-service-type-utils';
import { RedirectContext, XLRStartSequenceState } from 'src/xlr-configuration/xlr-startup';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { TouchPointNotFound } from '../../common/helpers/error-code';
import { getTouchpointTheme } from '../business-logic/service/get-touchpoint-theme';

const TouchPointStart: React.FC = (): JSX.Element => {
  const isEmerald = useIsEmeraldSellingService();
  const { setStartupState } = useContext(RedirectContext);
  const { setTouchpointTheme } = useContext(ThemeContext);
  const startSequenceState = useRef<XLRStartSequenceState>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const initialized = useRef<boolean>(false);

  const defaultOptions: LottieComponentProps = {
    loop: false,
    autoplay: true,
    animationData: getLaunchAnimation(getMetaData().serviceType),
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const checkRedirect = useCallback((): void => {
    if (initialized.current && startSequenceState.current) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
      setStartupState(startSequenceState.current);
    }
  }, [setStartupState]);

  useEffect(() => {
    let mounted = true;
    delay(7000).then((): void => {
      if (mounted) {
        timeoutRef.current = setInterval((): void => {
          checkRedirect();
        }, 500);
      }
    });
    return () => {
      mounted = false;
    };
  }, [timeoutRef, checkRedirect]);

  const startupCallback = useCallback(async () => {
    isEmerald ? await emeraldStartupSequence() : await r10StartupSequence();
    startSequenceState.current = XLRStartSequenceState.Login;
    initialized.current = true;

    if (skipAnimations()) {
      checkRedirect();
    }
  }, [isEmerald, checkRedirect]);

  useEffect(() => {
    getTouchpointTheme()
      .then(touchpointTheme => {
        setTouchpointTheme(touchpointTheme);
      })
      .catch(err => {
        XLRLogger.error({
          message:
            'Unable to fetch touchpoint theme from config service. Not applying a custom theme',
          source: 'touchpoint-configuration.ts',
          data: err,
        });
      });
  }, [setTouchpointTheme]);

  useEffect((): void => {
    startupCallback().catch((err): void => {
      if (err.code === TouchPointNotFound) {
        startSequenceState.current = XLRStartSequenceState.Setup;
        initialized.current = true;
      } else {
        const modal: IMessageModal = {
          acceptButtonLabel: 'Restart POS',
          hideCancelButton: true,
          onAccept: () => {
            window.location.reload();
          },
          type: EmeraldDialogTypes.Error,
          message: 'PosStartError',
          subMessage: err.message,
        };
        showEmeraldDialog(modal);
      }
    });
  }, [startupCallback, setStartupState]);

  return (
    <>
      <StartupAnimation lottieProps={defaultOptions} />;
    </>
  );
};

export default TouchPointStart;
